function clamp(value, min, max) {
    if (max < min)
        [min, max] = [max, min];
    return Math.min(Math.max(value, min), max);
}
/** Like clamp, but lets you pass potentially null values to min and max. */
function clampOptional(value, min, max) {
    value = min == null ? value : Math.max(value, min);
    return max == null ? value : Math.min(value, max);
}
/**
 * Tolerance for deciding whether a value sits on the step grid. Generous enough to absorb the
 * float drift `snap` introduces (which exceeds `Number.EPSILON` for values above ~1), while still
 * far smaller than the gap between adjacent snaps.
 */
const STEP_GRID_TOLERANCE = 1e-6;
/** Snap the given `value` to multiples of `step`. */
function snap(value, step) {
    return Math.round(value / step) * step;
}
/**
 * Convenience function that first snaps a value to `step`, and then clamps it within [min, max].
 */
function snapClamp(value, step, min, max) {
    return clamp(snap(value, step), min, max);
}
/**
 * Compute the percentage [0-1] that `value` represents between `min` and `max`. The `value` is
 * first clamped to the [min, max] range, so the result is always between 0 and 1.
 */
function computePercent(value, min, max) {
    const range = max - min;
    if (range === 0)
        return 0;
    value = clamp(value, min, max);
    return (value - min) / range;
}
/** Compute the value a percent represents for the given [`min`, `max`] range. */
function percentToValue(percent, min, max) {
    return percent * (max - min) + min;
}
/**
 * Increment value by delta, snapping to the nearest multiple of `step` in the direction of the
 * delta. Assumes that `delta` is a multiple of `step`.
 */
function deltaSnap(value, delta, step) {
    const valueSteps = value / step;
    const nearestStep = Math.round(valueSteps);
    // If `value` is already on a step boundary (modulo floating-point drift), do the arithmetic in
    // step-space. The integer add is exact, so error doesn't accumulate across iterations — which is
    // what caused e.g. repeatedly stepping by 0.1 with step 0.01 to lose a snap once `value` drifted
    // to -0.7000000000000001 and `(value + delta) / step` landed just past an integer.
    // The tolerance only needs to distinguish "aligned to step" from "between snaps", and the
    // gap between those is up to 0.5 in step-space — so the exact value isn't load-bearing.
    if (Math.abs(valueSteps - nearestStep) < STEP_GRID_TOLERANCE) {
        const deltaSteps = Math.round(delta / step);
        return (nearestStep + deltaSteps) * step;
    }
    // `value` is genuinely between snaps; floor/ceil toward the delta direction so we don't skip
    // the next closest snap (e.g. value 0.8 with delta 1 and step 1 should land at 1, not 2).
    const sign = Math.sign(delta);
    const fn = sign === -1 ? Math.ceil : sign === 1 ? Math.floor : Math.round;
    return fn((value + delta) / step) * step;
}
/**
 * Finds the the item in the array with the smallest result returned by the predicate function.
 * Requires that the `items` array is not empty.
 * Returns both the item and the result of the predicate function.
 */
function minByPredicate(items, pred) {
    let minItem = items[0];
    let minValue = pred(minItem);
    for (let i = 1; i < items.length; ++i) {
        const item = items[i];
        const value = pred(item);
        if (value < minValue) {
            minValue = value;
            minItem = item;
        }
    }
    return [minItem, minValue];
}
/**
 * Check if two numbers are closer than `tolerance`, which defaults to `Number.EPSILON`.
 *
 * `Number.EPSILON` is an absolute tolerance only meaningful near magnitude 1; for larger values
 * (or accumulated float drift), pass a looser tolerance like `STEP_GRID_TOLERANCE`.
 */
function areClose(a, b, tolerance = Number.EPSILON) {
    return Math.abs(a - b) < tolerance;
}
/** Like the % operator, but ensures the result is always positive. */
function imod(num, mod) {
    return ((num % mod) + mod) % mod;
}

export { STEP_GRID_TOLERANCE, areClose, clamp, clampOptional, computePercent, deltaSnap, imod, minByPredicate, percentToValue, snap, snapClamp };
//# sourceMappingURL=math.js.map
